<!-- CardRecurringBrick.vue -->
<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import api from "@/services/api";

const props = defineProps({
  plan: { type: Object, required: true },
  user: { type: Object, required: true },
  couponCode: { type: String, default: "" },
  finalPrice: { type: Number, default: null },
});

const emit = defineEmits(["subscription-result"]);

const isLoading = ref(false);
const error = ref(null);
const brickInstance = ref(null);
const hasSubmitted = ref(false);

const unmountBrick = async () => {
  if (brickInstance.value) {
    await brickInstance.value.unmount();
    brickInstance.value = null;
  }
};

const formatCurrency = (value) => {
  return Number(value ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const buildRecurringResultPayload = (
  apiResult = {},
  fallbackSuccess = true
) => {
  const status = (apiResult?.status || "").toLowerCase();

  const creditAmount = Number(apiResult?.creditAmount ?? 0);
  const originalAmount = Number(
    apiResult?.originalAmount ?? props.plan?.price ?? 0
  );
  const chargedAmount = Number(apiResult?.chargedAmount ?? 0);

  // ✅ MUDANÇA:
  // Antes: hasProportionalCredit
  // Agora: crédito aplicado pode vir de crédito proporcional OU de credit_balance.
  const hasCreditApplied = creditAmount > 0;

  const isFreeUpgrade =
    apiResult?.isFreeUpgrade === true || status === "free_upgrade";

  // ✅ Mantido defensivamente.
  // Com a nova regra do backend, este caso não deve mais ocorrer para:
  // planPrice=20, credit_balance=0,30, chargeAmount=19,70.
  const creditNotUsableInRecurring =
    apiResult?.creditNotUsableInRecurring === true;
  const availableCreditBalance = Number(apiResult?.availableCreditBalance ?? 0);

  let message = "Assinatura criada com sucesso!";

  if (hasCreditApplied && isFreeUpgrade) {
    message = [
      "Plano alterado com sucesso.",
      "",
      "Seu crédito disponível cobriu o valor desta alteração.",
      "Nenhum pagamento foi necessário e sua nova assinatura já está ativa.",
      "",
      `Valor original: ${formatCurrency(originalAmount)}`,
      `Crédito aplicado: ${formatCurrency(creditAmount)}`,
      `Valor pago agora: ${formatCurrency(0)}`,
    ].join("\n");
  } else if (hasCreditApplied) {
    const nextCycleAmount = Number(
      apiResult?.nextCycleAmount ?? originalAmount
    );

    message = [
      "Assinatura criada com sucesso!",
      "",
      "Seu crédito disponível foi aplicado ao primeiro ciclo da nova assinatura.",
      "",
      `Valor original do plano: ${formatCurrency(originalAmount)}`,
      `Crédito aplicado: ${formatCurrency(creditAmount)}`,
      `Valor cobrado no 1º ciclo: ${formatCurrency(chargedAmount)}`,
      `Próximos ciclos: ${formatCurrency(nextCycleAmount)}.`,
    ].join("\n");
  } else if (creditNotUsableInRecurring) {
    message = [
      "Assinatura criada com sucesso!",
      "",
      `Você possui ${formatCurrency(
        availableCreditBalance
      )} de crédito na sua conta.`,
      "Esse crédito não pôde ser aplicado integralmente neste ciclo sem respeitar o valor mínimo de cobrança.",
      "O saldo continuará disponível para uso futuro.",
    ].join("\n");
  }

  return {
    success: fallbackSuccess,
    ...apiResult,
    isFreeUpgrade,

    // ✅ Mantém compatibilidade com quem já consome essa flag.
    // Mesmo com o nome antigo, agora ela significa apenas:
    // houve algum crédito aplicado no 1º ciclo.
    hasProportionalCredit: hasCreditApplied,

    // ✅ Nova flag mais correta semanticamente.
    hasCreditApplied,

    creditAmount,
    originalAmount,
    chargedAmount,
    creditNotUsableInRecurring,
    availableCreditBalance,
    planId: apiResult?.planId ?? props.plan?.planId ?? null,
    planName: apiResult?.planName ?? props.plan?.planName ?? props.plan?.name ?? null,
    value: chargedAmount || originalAmount || props.plan?.price || null,
    message,
  };
};

async function initializeBrick() {
  try {
    if (!window.MercadoPago) {
      error.value = "SDK do Mercado Pago não foi carregada.";
      return;
    }

    await unmountBrick();

    const YOUR_PUBLIC_KEY = process.env.VUE_APP_MP_PUBLIC_KEY;
    const PREAPPROVAL_ENDPOINT = "/payment/create-recurring-subscription";

    const mp = new window.MercadoPago(YOUR_PUBLIC_KEY, { locale: "pt-BR" });
    const bricksBuilder = mp.bricks();

    const amountToPay =
      props.finalPrice !== null
        ? props.finalPrice
        : Number(props.plan?.price ?? 0);

    brickInstance.value = await bricksBuilder.create(
      "cardPayment",
      "card-payment-preapproval-container",
      {
        initialization: {
          amount: amountToPay,

          // ── PATCH 1 ──────────────────────────────────────────────────────
          // ANTES: payer: { email: props.user?.email || "" }
          // Passar email faz o Brick buscar o Customer no MP e pré-carregar
          // cartões do vault. Quando há cartão salvo, o BIN fica null e a
          // bandeira retornada é a do cartão antigo (ex.: Visa do Itaú),
          // não a do cartão novo que o usuário está digitando agora.
          //
          // SOLUÇÃO: não passar email na initialization. O email ainda vai
          // ao backend via formData.payer.email no onSubmit — nada se perde.
          // ── FIM PATCH 1 ──────────────────────────────────────────────────
        },

        customization: {
          visual: { style: { theme: "default" } },

          // ── PATCH 2 ──────────────────────────────────────────────────────
          // Garante que o Brick opere em modo "novo cartão" sem oferecer
          // a opção de selecionar cartões salvos do vault do Customer.
          // Isso é uma camada extra de defesa além do Patch 1.
          // ── FIM PATCH 2 ──────────────────────────────────────────────────
          paymentMethods: {
            types: {
              excluded: ["debit_card"], // mantém só crédito, sem débito
            },
          },
        },

        callbacks: {
          onReady: () => console.log("✅ Brick Recorrência pronto"),

          onSubmit: async (formData) => {
            if (hasSubmitted.value) return;

            let shouldRecreateBrick = true;

            hasSubmitted.value = true;
            isLoading.value = true;
            error.value = null;

            try {
              const idNum = formData?.payer?.identification?.number || "";
              const idType = formData?.payer?.identification?.type || "CPF";

              // ── PATCH 3 ──────────────────────────────────────────────────
              // ANTES: payerEmail = formData?.payer?.email || props.user?.email
              // O email agora vem sempre do formData (digitado no Brick) com
              // fallback para props. Sem mudança de comportamento aqui, mas
              // explicitado para clareza após remover o email da initialization.
              // ── FIM PATCH 3 ──────────────────────────────────────────────
              const payerEmail =
                formData?.payer?.email || props.user?.email || "";

              const paymentMethodId =
                formData?.payment_method_id ||
                formData?.paymentMethodId ||
                formData?.payment_method ||
                null;

              const issuerId =
                formData?.issuer_id ||
                formData?.issuerId ||
                formData?.issuer ||
                null;

              // ── PATCH 4 ──────────────────────────────────────────────────
              // Log defensivo: registra o que o Brick realmente entregou.
              // Útil para confirmar que, após o patch, o payment_method_id
              // agora reflete o cartão digitado (ex.: "master") e não o
              // do vault (ex.: "visa"). Remova em produção quando confirmado.
              // ── FIM PATCH 4 ──────────────────────────────────────────────
              console.log(
                "[Brick] formData.payment_method_id:",
                paymentMethodId
              );
              console.log("[Brick] formData.issuer_id:", issuerId);
              console.log("[Brick] formData.token:", formData?.token);

              const payload = {
                planId: Number(props.plan?.planId),
                token: formData.token,
                couponCode: props.couponCode,
                paymentMethodId,
                issuerId,
                payer: {
                  email: payerEmail,
                  firstName:
                    formData?.payer?.first_name ||
                    props.user?.firstName ||
                    "Cliente",
                  lastName:
                    formData?.payer?.last_name ||
                    props.user?.lastName ||
                    "Teste",
                  identification: { type: idType, number: idNum },
                },
              };

              const { data } = await api.post(PREAPPROVAL_ENDPOINT, payload);

              const status = (data?.status || "").toLowerCase();

              const subscriptionId =
                data?.subscriptionId ||
                data?.newSubscriptionId ||
                data?.externalReference;

              const isFreeUpgrade =
                data?.isFreeUpgrade === true || status === "free_upgrade";

              if (status === "authorized" || isFreeUpgrade) {
                shouldRecreateBrick = false;

                emit(
                  "subscription-result",
                  buildRecurringResultPayload(
                    {
                      ...data,
                      subscriptionId,
                      status,
                    },
                    true
                  )
                );

                await unmountBrick();
                return;
              } else if (status === "pending") {
                emit("subscription-result", {
                  success: false,
                  subscriptionId,
                  status,
                  message: "Aguardando autorização.",
                });
              } else {
                emit("subscription-result", {
                  success: false,
                  status,
                  message: data?.statusDetail || "Erro na assinatura.",
                });
              }

              await unmountBrick();
            } catch (err) {
              const msg =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                "Falha ao criar assinatura.";

              emit("subscription-result", { success: false, message: msg });
            } finally {
              isLoading.value = false;
              hasSubmitted.value = false;

              if (shouldRecreateBrick) {
                await nextTick();
                await unmountBrick();
                await initializeBrick();
              }
            }
          },

          onError: (err) => {
            error.value = err?.message;
          },
        },
      }
    );
  } catch (e) {
    error.value = "Erro ao carregar componente.";
  }
}

watch(
  () => props.finalPrice,
  async () => {
    // ✅ MUDANÇA: não recria o Brick durante submit
    if (isLoading.value) return;

    await nextTick();
    await initializeBrick();
  }
);

onMounted(async () => {
  if (props.plan && props.user) {
    await nextTick();
    await initializeBrick();
  }
});

onUnmounted(() => {
  unmountBrick();
});
</script>

<template>
  <div>
    <div id="card-payment-preapproval-container"></div>
    <div v-if="error" class="mt-4 p-4 bg-red-50 text-red-600 rounded-lg">
      {{ error }}
    </div>
  </div>
</template>
